import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, Clock } from "lucide-react";
import { WA_DEFAULT, BRAND, fbqTrack } from "../lib/site";
import { useLead } from "./LeadModal";

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16 2.7C8.7 2.7 2.8 8.6 2.8 15.9c0 2.6.8 5 2.1 7L2.7 29.3l6.6-2.1c1.9 1.2 4.2 1.9 6.7 1.9 7.3 0 13.2-5.9 13.2-13.2S23.3 2.7 16 2.7zm0 24c-2.2 0-4.3-.6-6.1-1.8l-.4-.3-3.9 1.2 1.2-3.8-.3-.4c-1.3-1.9-2-4.1-2-6.4C4.5 9.6 9.7 4.4 16 4.4s11.5 5.2 11.5 11.5S22.3 26.7 16 26.7zm6.3-8.6c-.3-.2-2-1-2.4-1.1-.3-.1-.6-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.3-.8.1-.3-.2-1.5-.5-2.8-1.7-1-0.9-1.7-2.1-1.9-2.4-.2-.3 0-.5.2-.7l.6-.7c.2-.2.2-.4.3-.6.1-.2 0-.4 0-.6-.1-.2-.8-2-1.1-2.7-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.6.1-1 .5-.3.3-1.2 1.2-1.2 2.9s1.3 3.4 1.4 3.6c.2.2 2.5 3.8 6 5.3.8.4 1.5.6 2 .7.8.3 1.6.2 2.2.1.7-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.3-.7-.4z" />
  </svg>
);

export const WhatsAppFloat = () => {
  const [callOpen, setCallOpen] = useState(false);
  const { openLead } = useLead();

  return (
    <>
      <AnimatePresence>
        {callOpen && (
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-44 right-5 z-[95] w-72 border border-navy-900/10 bg-white shadow-[0_24px_60px_rgb(8,16,39,0.35)] sm:right-6"
            data-testid="call-popup"
            role="dialog"
            aria-label="Call Estate Paradise"
          >
            <div className="flex items-center justify-between bg-navy-900 px-5 py-3.5">
              <p className="font-serif text-base font-medium text-white">Talk to an Advisor</p>
              <button
                onClick={() => setCallOpen(false)}
                aria-label="Close call popup"
                data-testid="call-popup-close"
                className="flex h-7 w-7 items-center justify-center text-white/60 transition-colors duration-300 hover:text-gold-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="font-serif text-xl font-medium text-navy-900">{BRAND.phone}</p>
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-gray-400">
                <Clock className="h-3.5 w-3.5 text-gold-600" />
                Mon – Sun, 10 AM – 7 PM
              </p>
              <a
                href={`tel:+${BRAND.phoneRaw}`}
                data-testid="call-now-btn"
                onClick={() => fbqTrack("Contact", { content_name: "Call Now" })}
                className="mt-4 flex w-full items-center justify-center gap-2 bg-gold-500 px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-400"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
              <button
                data-testid="request-callback-btn"
                onClick={() => {
                  setCallOpen(false);
                  openLead({ title: "Request a Callback", subtitle: "Share your details — we call you back within 30 minutes.", source: "Call Popup" });
                }}
                className="mt-2.5 w-full border border-navy-900/25 px-4 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-colors duration-300 hover:border-navy-900 hover:bg-navy-900 hover:text-white"
              >
                Request a Callback
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setCallOpen(!callOpen)}
        data-testid="call-float-btn"
        aria-label="Call Estate Paradise"
        initial={{ opacity: 0, scale: 0.5, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.3, type: "spring", stiffness: 200, damping: 16 }}
        whileHover={{ scale: 1.1, rotate: -4 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-[5.75rem] right-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-navy-900 text-gold-400 shadow-[0_14px_34px_rgb(13,27,62,0.45)]"
      >
        <Phone className="h-6 w-6" />
      </motion.button>

      <motion.a
        href={WA_DEFAULT}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        aria-label="Chat with Estate Paradise on WhatsApp"
        onClick={() => fbqTrack("Contact", { content_name: "WhatsApp Float" })}
        initial={{ opacity: 0, scale: 0.5, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 1.4, type: "spring", stiffness: 200, damping: 16 }}
        whileHover={{ scale: 1.1, rotate: 4 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_14px_34px_rgb(37,211,102,0.45)]"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366]/40 [animation-duration:2.4s]" aria-hidden="true" />
        <WhatsAppGlyph className="relative h-7 w-7" />
      </motion.a>
    </>
  );
};
